import React, { useEffect, useState } from "react";
import "./Upgrade.css";
import Header from "../../Components/Shared/Header/Header";
import Footer from "../../Components/Shared/Footer/Footer";
import VivahApi from "../../api";
import { useNavigate } from "react-router-dom";
import { isAuthenticated } from "../../utils/api-utils";
import authService from "../../services/auth.service";
import Swal from "sweetalert2";

const PLAN_ICONS = {
  silver: "🥈", gold: "🥇", diamond: "💎", platinum: "👑",
};

function getPlanIcon(name = "") {
  const key = name.toLowerCase();
  for (const k of Object.keys(PLAN_ICONS)) {
    if (key.includes(k)) return PLAN_ICONS[k];
  }
  return "🎁";
}

const DEFAULT_FEATURES = [
  "Send & receive interests",
  "View full profiles",
  "Direct messaging",
  "Priority support",
];

function Upgrade() {
  const [plans, setPlans]           = useState([]);
  const [loading, setLoading]       = useState(true);
  const [error, setError]           = useState(null);
  const [currentPlan, setCurrentPlan] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    const init = async () => {
      try {
        const res = await VivahApi.Plan.listPlan();
        setPlans(res.data?.plans || res.data || []);

        // Load current user's plan status
        if (isAuthenticated()) {
          try {
            const me = await VivahApi.Auth.getUser();
            const profile = me?.data?.profile || me?.data;
            if (profile?.isPremium === "yes" && profile?.premiumPlanExpiry) {
              const expiry = new Date(profile.premiumPlanExpiry);
              if (expiry > new Date()) {
                setCurrentPlan({
                  planId:  profile.premiumPlanId,
                  expiry:  profile.premiumPlanExpiry,
                  name:    plans.find(p => p.planId === profile.premiumPlanId)?.name || "Premium",
                });
              }
            }
          } catch (_) { /* ignore profile load errors */ }
        }
      } catch (err) {
        setError("Could not load plans. Please try again.");
      } finally {
        setLoading(false);
      }
    };
    init();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSelect = (plan) => {
    if (!isAuthenticated()) {
      Swal.fire({
        title: "Login required",
        text: "Please log in to purchase a plan.",
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#e91e63",
        cancelButtonColor: "#aaa",
        confirmButtonText: "Go to Login",
      }).then((r) => { if (r.isConfirmed) navigate("/login"); });
      return;
    }
    navigate("/checkout", { state: { plan } });
  };

  // Mark the middle plan as "popular"
  const popularIdx = plans.length >= 3 ? Math.floor(plans.length / 2) : -1;

  return (
    <>
      <div className="upgrade-page">
        <Header />

        {/* Hero */}
        <div className="upgrade-hero">
          <div className="upgrade-hero__badge">Membership Plans</div>
          <h1 className="upgrade-hero__title">
            Upgrade your <span>Vivah Bandh</span> experience
          </h1>
          <p className="upgrade-hero__sub">
            Connect with thousands of verified profiles and find your perfect life-partner.
          </p>
        </div>

        {/* Current plan banner */}
        {currentPlan && (
          <div className="upgrade-current" style={{ margin: "0 auto 32px" }}>
            <span className="upgrade-current__icon">✅</span>
            <div className="upgrade-current__text">
              You have an active <strong>{currentPlan.name}</strong> plan.{" "}
              Expires on <strong>{currentPlan.expiry}</strong>.
            </div>
          </div>
        )}

        {loading && <div className="upgrade-loading">Loading plans…</div>}
        {error   && <div className="upgrade-error">{error}</div>}

        {!loading && !error && (
          <div className="upgrade-plans">
            {plans.map((plan, idx) => {
              const features = plan.features?.length
                ? plan.features
                : DEFAULT_FEATURES;
              const isPopular = idx === popularIdx;
              return (
                <div
                  key={plan.planId}
                  className={`plan-card${isPopular ? " plan-card--popular" : ""}`}
                >
                  <div className="plan-card__icon">{getPlanIcon(plan.name)}</div>
                  <div className="plan-card__name">{plan.name}</div>
                  <div className="plan-card__duration">
                    {plan.durationDays} days · {plan.durationLabel || ""}
                  </div>
                  <div className="plan-card__price">
                    ₹{plan.price?.toLocaleString("en-IN")}
                    <span> /plan</span>
                  </div>
                  <hr className="plan-card__divider" />
                  <ul className="plan-card__features">
                    {features.map((f, i) => <li key={i}>{f}</li>)}
                  </ul>
                  <button
                    className="plan-card__btn"
                    onClick={() => handleSelect(plan)}
                    disabled={
                      currentPlan?.planId === plan.planId
                    }
                  >
                    {currentPlan?.planId === plan.planId
                      ? "Current Plan"
                      : "Get Started"}
                  </button>
                </div>
              );
            })}
          </div>
        )}

        <Footer />
      </div>
    </>
  );
}

export default Upgrade;
